import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * Representa um responsável
 * 
 * @typedef {Object} Responsavel
 * @property {number} id - Identificador único
 * @property {string} nome - Nome do responsável
 * @property {string} email - Email do responsável
 * @property {number|null} usuario_id - ID do usuário vinculado (pode ser nulo)
 * @property {Object|null} usuario - Dados do usuário (pode ser nulo)
 * @property {number|null} nivel_hierarquico_id - ID do nível hierárquico
 * @property {Object|null} nivel_hierarquico - Nível hierárquico
 * @property {number|null} business_unit_id - ID da business unit
 * @property {Object|null} business_unit - Business unit
 */

/**
 * Representa o payload de criação/atualização de um responsável
 * 
 * @typedef {Object} ResponsavelPayload
 * @property {number} nivel_hierarquico_id - ID do nível hierárquico (obrigatório)
 * @property {number} business_unit_id - ID da business unit (obrigatório)
 * @property {string} nome - Nome do responsável (obrigatório)
 * @property {string} email - Email do responsável (obrigatório)
 */

const resource = {
  base: (empresaId) => `v1/ativacaoped/${empresaId}/responsaveis`,
  item: (empresaId, responsavelId) => `v1/ativacaoped/${empresaId}/responsaveis/${responsavelId}`
};

const LOADING_KEY = 'ativacaoped-responsaveis';

/**
 * Lista responsáveis com paginação e filtros.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object|string} [params] - Parâmetros de consulta (objeto) ou query string já montada
 * @param {number} [params.page] - Número da página
 * @param {number} [params.per_page] - Itens por página
 * @returns {Promise<PaginacaoLaravelResource<Responsavel>>} Lista paginada de responsáveis
 */
export const getResponsaveis = (empresaId, params = { page: 1, per_page: 10 }) => {
  // Aceita a query string crua montada pelo MasterDetail (page/per_page/sort/filters).
  const query = typeof params === "string" ? params : parseParams(params);
  let url = resource.base(empresaId);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Obtém um responsável específico pelo ID.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} responsavelId - ID do responsável
 * @returns {Promise<Responsavel>} Dados do responsável
 */
export const getResponsavel = (empresaId, responsavelId) => {
  const baseUrl = resource.item(empresaId, responsavelId);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl).then(({ data }) => data.data)
  );
};

/**
 * Lista responsáveis simplificados para componentes de seleção (dropdown, autocomplete).
 *
 * Cada item pode trazer `nivel_hierarquico_id` e o nível hierárquico
 * (`nivel_hierarquico` com `id`, `nome` e `tipo_id`), usados para agrupar os
 * candidatos na seleção de revisores do workflow. Quando o backend não devolve
 * esses campos, a tela mantém a lista plana (failsafe).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @param {number} [params.workflow_id] - Filtro por workflow
 * @param {number} [params.business_plan_id] - Filtro por business plan
 * @returns {Promise<Responsavel[]>} Lista de responsáveis
 */
export const getSelecaoResponsaveis = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.base(empresaId).concat(`/selecao`);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data.data || data)
  );
};

/**
 * Cria um novo responsável.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {ResponsavelPayload} data - Dados do responsável a ser criado
 * @returns {Promise<Responsavel>} Responsável criado
 */
export const postResponsavel = (empresaId, data) => {
  const baseUrl = resource.base(empresaId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Atualiza um responsável existente (backend usa POST para update).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} responsavelId - ID do responsável a ser atualizado
 * @param {ResponsavelPayload} data - Dados parciais do responsável para atualização
 * @returns {Promise<Responsavel>} Responsável atualizado
 */
export const putResponsavel = (empresaId, responsavelId, data) => {
  const baseUrl = resource.item(empresaId, responsavelId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Exclui um responsável.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} responsavelId - ID do responsável a ser excluído
 * @returns {Promise<void>}
 */
export const deleteResponsavel = (empresaId, responsavelId) => {
  const baseUrl = resource.item(empresaId, responsavelId);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl).then(({ data }) => data)
  );
};

/**
 * Solicita a exportação de responsáveis.
 *
 * @param {number|string} empresaId - ID da empresa
 * @returns {Promise<{url: string}>} URL da exportação
 */
export const getExportResponsaveis = (empresaId) => {
  const baseUrl = resource.base(empresaId).concat(`/export`);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl).then(({ data }) => data)
  );
};

export default {
  getResponsavel,
  getResponsaveis,
  getSelecaoResponsaveis,
  postResponsavel,
  putResponsavel,
  deleteResponsavel,
  getExportResponsaveis
};